import React, { useState, useEffect, useCallback } from 'react';
import { useDbUser } from '../hooks/useDbUser';
import { db, Document, Matter, Project, DocumentFolder, DocumentAction, Client } from '../lib/db';
import { Upload, File, Mail, FileText, X, Loader, Clock, CheckCircle, Calendar, Target, Users, Briefcase, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import DocumentPreview from '../components/documents/DocumentPreview';
import DocumentFilingPanel from '../components/documents/DocumentFilingPanel';
import { ProcessingPanel } from '../components/documents/ProcessingPanel';
import { useTimer } from '../contexts/TimerContext';
import { format } from 'date-fns';

function InboxPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const { startTimer, setUserId } = useTimer();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filedDocuments, setFiledDocuments] = useState<Document[]>([]);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  // Processing is now handled by ProcessingPanel component
  const [activeTab, setActiveTab] = useState<'filing' | 'processing'>('filing');
  const [documentActions, setDocumentActions] = useState<Map<string, DocumentAction[]>>(new Map());
  const [selectedProcessingDoc, setSelectedProcessingDoc] = useState<Document | null>(null);

  useEffect(() => {
    if (dbUserId) {
      loadData();
      setUserId(dbUserId);
    }
  }, [dbUserId]);

  const loadData = async () => {
    if (!dbUserId) return;
    
    try {
      const [unfiledDocs, allDocs, allMatters, allProjects, allClients] = await Promise.all([
        db.documents.findUnfiled(dbUserId),
        db.documents.findByUserId(dbUserId),
        db.matters.findByUserId(dbUserId),
        db.projects.findByUserId(dbUserId),
        db.clients.findByUserId(dbUserId)
      ]);
      
      // Filter filed documents
      const filedDocs = allDocs.filter(doc => 
        doc.status === 'filed' || doc.status === 'processing'
      );
      
      setDocuments(unfiledDocs);
      setFiledDocuments(filedDocs);
      setMatters(allMatters);
      setProjects(allProjects);
      setClients(allClients);
      
      // Load actions for filed documents
      const actionsMap = new Map<string, DocumentAction[]>();
      for (const doc of filedDocs) {
        const actions = await db.documentActions.findByDocumentId(doc.id, dbUserId);
        if (actions.length > 0) {
          actionsMap.set(doc.id, actions);
        }
      }
      setDocumentActions(actionsMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const uploadToVercel = async (file: File): Promise<string> => {
    try {
      // Upload to Vercel Blob Storage via API route
      const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        body: file,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed', details: 'Unknown error' }));
        console.error('Upload API error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Upload failed');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Upload error:', error);
      
      // Fallback to data URL for small files in development
      if (import.meta.env.DEV && file.size < 1024 * 1024) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              reject(new Error('Failed to read file'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      throw error;
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!dbUserId) return;
    
    setIsUploading(true);
    
    try {
      for (const file of Array.from(files)) {
        // Validate file size (e.g., 10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 10MB.`);
          continue;
        }
        
        // Upload to Vercel Blob
        const blobUrl = await uploadToVercel(file);
        
        // Generate suggested filename with date prefix
        const uploadDate = new Date();
        const datePrefix = uploadDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const fileExtension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
        const fileNameWithoutExt = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
        const suggestedFilename = `${datePrefix} ${fileNameWithoutExt}${fileExtension}`;
        
        // Create document record
        const document = await db.documents.create({
          user_id: dbUserId,
          filename: file.name,
          original_filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
          blob_url: blobUrl,
          status: 'unfiled',
          suggested_filename: suggestedFilename,
          metadata: {
            uploaded_via: 'inbox',
            original_date: new Date().toISOString()
          }
        });
        
        toast.success(`${file.name} uploaded successfully`);
      }
      
      // Reload documents
      await loadData();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  }, [dbUserId]);

  const handleDocumentSelect = async (document: Document) => {
    setSelectedDocument(document);
    
    // Start timer automatically when reviewing a document
    if (dbUserId) {
      // Create a review session
      await db.documentReviewSessions.create({
        user_id: dbUserId,
        document_id: document.id,
        started_at: new Date()
      });
      
      // Start timer with document review task
      startTimer({
        id: `doc-review-${document.id}`,
        title: `Review: ${document.original_filename}`,
        description: 'Document review',
        status: 'In Progress',
        priority: 'Medium',
        user_id: dbUserId,
        position: 0,
        created_at: new Date(),
        updated_at: new Date()
      }, undefined, undefined, undefined, undefined, dbUserId);
    }
  };

  const handleDocumentFiled = async () => {
    // Reload documents after filing
    await loadData();
    
    // Switch to processing tab
    setActiveTab('processing');
    
    // Don't automatically show processing modal - let user process when ready
    setSelectedDocument(null);
  };

  const handleFileAttachment = async (attachment: any) => {
    try {
      // Convert base64 data to blob
      const base64Data = attachment.data;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.contentType || 'application/octet-stream' });

      // Create a file from the blob
      const file = new File([blob], attachment.filename, {
        type: attachment.contentType || 'application/octet-stream'
      });

      // Upload the file
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload attachment');
      }

      const { url } = await response.json();

      // Create a document record for the attachment
      const newDocument = await db.documents.create({
        user_id: dbUserId!,
        filename: attachment.filename,
        original_filename: attachment.filename,
        mime_type: attachment.contentType || 'application/octet-stream',
        size_bytes: attachment.size,
        blob_url: url,
        status: 'unfiled',
        description: `Attachment from email: ${selectedDocument?.original_filename || 'Unknown'}`
      });

      toast.success(`Attachment "${attachment.filename}" uploaded successfully`);
      
      // Refresh the documents list
      await loadData();
      
      // Select the new document for filing
      setSelectedDocument(newDocument);
    } catch (error) {
      console.error('Error filing attachment:', error);
      toast.error('Failed to file attachment');
    }
  };

  // Removed handleProcessDocument - processing is now handled by ProcessingPanel

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create_deadline':
        return <Calendar className="w-4 h-4 text-red-600" />;
      case 'create_deliverable':
        return <Target className="w-4 h-4 text-green-600" />;
      case 'create_matter':
        return <FolderOpen className="w-4 h-4 text-blue-600" />;
      case 'create_client':
        return <Users className="w-4 h-4 text-purple-600" />;
      case 'create_project':
        return <Briefcase className="w-4 h-4 text-orange-600" />;
      default:
        return null;
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'create_deadline':
        return 'Created deadline';
      case 'create_deliverable':
        return 'Created deliverable';
      case 'create_matter':
        return 'Created matter';
      case 'create_client':
        return 'Created client';
      case 'create_project':
        return 'Created project';
      default:
        return actionType;
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <FileText className="w-8 h-8 text-blue-500" />;
    if (mimeType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-8 h-8 text-blue-600" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText className="w-8 h-8 text-green-600" />;
    if (mimeType.includes('message/rfc822') || mimeType.includes('email')) return <Mail className="w-8 h-8 text-purple-500" />;
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 overflow-hidden">
      <div className="flex flex-col lg:flex-row h-full">
        {/* Left Panel - Document List */}
        <div className="w-full lg:w-1/3 bg-white border-b lg:border-r lg:border-b-0 border-gray-200 flex flex-col h-full overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
            <p className="text-sm text-gray-600 mt-1">
              {activeTab === 'filing' ? 'Upload and file documents' : 'Process filed documents'}
            </p>
          </div>
          
          {/* Sub-tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => {
                setActiveTab('filing');
                setSelectedProcessingDoc(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'filing'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Filing ({documents.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('processing');
                setSelectedDocument(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'processing'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Processing ({filedDocuments.length})
            </button>
          </div>
          
          {/* Tab Content */}
          {activeTab === 'filing' ? (
            <>
              {/* Upload Area */}
              <div
                className={`m-4 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  Drag and drop files here, or click to browse
                </p>
                <input
                  type="file"
                  multiple
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.eml,.msg,.txt,.png,.jpg,.jpeg"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 cursor-pointer"
                >
                  Select Files
                </label>
              </div>
              
              {/* Unfiled Document List */}
              <div className="flex-1 overflow-y-auto">
                {documents.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p>No unfiled documents</p>
                    <p className="text-sm mt-2">Upload documents to get started</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        onClick={() => handleDocumentSelect(doc)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedDocument?.id === doc.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          {getFileIcon(doc.mime_type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.suggested_filename || doc.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(doc.size_bytes)} • {new Date(doc.upload_date).toLocaleDateString()}
                            </p>
                            {doc.suggested_filename && doc.suggested_filename !== doc.original_filename && (
                              <p className="text-xs text-gray-400 truncate">
                                Original: {doc.original_filename}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Processing Tab Content */
            <div className="flex-1 overflow-y-auto">
              {filedDocuments.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p>No documents awaiting processing</p>
                  <p className="text-sm mt-2">File documents from the Filing tab to begin processing</p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {filedDocuments.map((doc) => {
                    const actions = documentActions.get(doc.id) || [];
                    const isProcessing = doc.status === 'processing';
                    
                    return (
                      <div
                        key={doc.id}
                        onClick={() => setSelectedProcessingDoc(doc)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedProcessingDoc?.id === doc.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <FileText className="w-8 h-8 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.suggested_filename || doc.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              Filed on {format(new Date(doc.upload_date), 'MMM d, yyyy')}
                            </p>
                            {actions.length > 0 && (
                              <p className="text-xs text-indigo-600 mt-1">
                                {actions.length} action{actions.length !== 1 ? 's' : ''} taken
                              </p>
                            )}
                          </div>
                          <div className="flex items-center">
                            {isProcessing ? (
                              <Loader className="w-5 h-5 text-gray-600 animate-spin" />
                            ) : (
                              <Clock className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Right Panel - Document Preview and Actions */}
        <div className="flex-1 flex flex-col lg:flex-row min-w-0 h-full">
          {activeTab === 'filing' ? (
            selectedDocument ? (
            <>
              {/* Mobile: Show preview and filing in tabs */}
              <div className="lg:hidden w-full h-full">
                <div className="flex border-b border-gray-200 bg-white">
                  <button
                    className="flex-1 py-3 text-sm font-medium border-b-2 border-indigo-500 text-indigo-600"
                  >
                    Preview
                  </button>
                  <button
                    className="flex-1 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      // In a real implementation, switch to filing tab
                      toast.info('Filing panel is optimized for desktop view');
                    }}
                  >
                    File Document
                  </button>
                </div>
                <div className="flex-1 bg-gray-100 p-4 overflow-hidden">
                  <DocumentPreview document={selectedDocument} onFileAttachment={handleFileAttachment} />
                </div>
              </div>
              
              {/* Desktop: Side-by-side preview and filing */}
              <div className="hidden lg:flex lg:flex-1 h-full overflow-hidden">
                {/* Document Preview */}
                <div className="flex-1 bg-gray-100 p-4 min-w-0 flex flex-col overflow-hidden">
                  <DocumentPreview document={selectedDocument} onFileAttachment={handleFileAttachment} />
                </div>
                
                {/* Filing Panel */}
                <div className="w-96 bg-white border-l border-gray-200 flex-shrink-0 flex flex-col h-full overflow-hidden">
                  <DocumentFilingPanel
                    document={selectedDocument}
                    matters={matters}
                    projects={projects}
                    clients={clients}
                    dbUserId={dbUserId!}
                    onFiled={handleDocumentFiled}
                    onClose={() => setSelectedDocument(null)}
                  />
                </div>
              </div>
            </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Select a document to preview</p>
                </div>
              </div>
            )
          ) : (
            /* Processing Tab Right Panel */
            selectedProcessingDoc ? (
              <div className="flex-1 flex h-full overflow-hidden">
                {/* Document Preview */}
                <div className="flex-1 bg-gray-100 p-4 overflow-hidden">
                  <DocumentPreview document={selectedProcessingDoc} onFileAttachment={handleFileAttachment} />
                </div>
                
                {/* Processing Actions Panel */}
                <div className="w-96 bg-white border-l border-gray-200 p-6 overflow-y-auto">
                  <ProcessingPanel
                    document={selectedProcessingDoc}
                    dbUserId={dbUserId!}
                    documentActions={documentActions.get(selectedProcessingDoc.id) || []}
                    onUpdate={loadData}
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-100 h-full">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Select a document to preview and process</p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      
      {/* Upload Progress Overlay */}
      {isUploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <Loader className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-gray-700">Uploading files...</p>
          </div>
        </div>
      )}
      
      {/* Processing Modal - removed as processing is now handled by ProcessingPanel */}
    </div>
  );
}

export default InboxPage;